import type {
  ChapterComment,
  DiscussionContentActionRequest,
  DiscussionCsCommentIdentity,
  DiscussionUserBulkDeleteJob,
  DiscussionUserEnforcement,
  ListDiscussionCommentsQuery,
  ListDiscussionReportsQuery,
} from './chapterDiscussions.types'

function compact<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(
      ([, item]) => item !== undefined && item !== '',
    ),
  ) as T
}

function assertPositive(value: number | undefined, label: string): void {
  if (value !== undefined && (!Number.isSafeInteger(value) || value <= 0)) {
    throw new Error(`${label} must be a positive integer`)
  }
}

function normalizeMediaQuery<
  T extends { mediaType?: 'book'; bookId?: number; chapterId?: number },
>(query: T): T {
  assertPositive(query.bookId, 'Book')
  assertPositive(query.chapterId, 'Chapter')
  if (query.chapterId !== undefined && query.bookId === undefined) {
    throw new Error('Book is required before filtering by chapter')
  }
  if (
    (query.bookId !== undefined || query.chapterId !== undefined) &&
    query.mediaType === undefined
  ) {
    return { ...query, mediaType: 'book' }
  }
  return query
}

export function buildCommentQuery(
  input: ListDiscussionCommentsQuery,
): ListDiscussionCommentsQuery {
  assertPositive(input.authorId, 'Author')
  const query = normalizeMediaQuery(compact({ ...input }))
  const families = [
    query.moderationStatus !== undefined,
    query.visibilityStatus !== undefined,
    query.mediaType !== undefined ||
      query.bookId !== undefined ||
      query.chapterId !== undefined,
    query.authorId !== undefined,
    query.exactCommentId !== undefined,
    query.policyReason !== undefined,
  ].filter(Boolean)
  if (families.length > 1) {
    throw new Error('Use only one filter group at a time')
  }
  return query
}

export function buildReportQuery(
  input: ListDiscussionReportsQuery,
): ListDiscussionReportsQuery {
  assertPositive(input.reporterUserId, 'Reporter')
  assertPositive(input.reportedAuthorId, 'Author')
  const query = normalizeMediaQuery(compact({ ...input }))
  const families = [
    query.status !== undefined,
    query.reason !== undefined,
    query.reporterUserId !== undefined,
    query.mediaType !== undefined ||
      query.bookId !== undefined ||
      query.chapterId !== undefined,
    query.reportedAuthorId !== undefined,
    query.exactCommentId !== undefined,
  ].filter(Boolean)
  if (families.length > 1) {
    throw new Error('Use only one filter group at a time')
  }
  return query
}

export function discussionCommentIdentityKey(
  identity: DiscussionCsCommentIdentity,
): string {
  return encodeURIComponent(JSON.stringify(identity))
}

export function parseDiscussionCommentIdentityKey(
  value: string,
): DiscussionCsCommentIdentity {
  let parsed: unknown
  try {
    parsed = JSON.parse(value)
  } catch {
    try {
      parsed = JSON.parse(decodeURIComponent(value))
    } catch {
      throw new Error('Invalid discussion comment identity')
    }
  }
  const identity = parsed as Partial<DiscussionCsCommentIdentity>
  if (
    identity.mediaType !== 'book' ||
    !Number.isSafeInteger(identity.bookId) ||
    !Number.isSafeInteger(identity.chapterId) ||
    typeof identity.commentId !== 'string' ||
    !identity.commentId
  ) {
    throw new Error('Invalid discussion comment identity')
  }
  return identity as DiscussionCsCommentIdentity
}

export function buildContentActionRequest(input: {
  action: DiscussionContentActionRequest['action']
  reason: string
  policyVersion: string
  comment: ChapterComment
}): DiscussionContentActionRequest {
  return {
    action: input.action,
    reason: input.reason.trim(),
    policyVersion: input.policyVersion.trim(),
    expected: compact({
      contentRevision: input.comment.contentRevision,
      moderatedRevision: input.comment.moderatedRevision,
      visibilityStatus: input.comment.visibilityStatus,
      decisionSource: input.comment.decisionSource,
      replyTargetRevision: input.comment.moderatedParentRevision,
    }),
  }
}

export function canUnblockDiscussionUser(
  enforcement: DiscussionUserEnforcement | null,
  currentJob: DiscussionUserBulkDeleteJob | null,
): boolean {
  if (!enforcement || enforcement.status !== 'blocked') return false
  if (!enforcement.currentBulkDeleteJobId) return true
  return (
    currentJob?.jobId === enforcement.currentBulkDeleteJobId &&
    currentJob.enforcementGeneration === enforcement.generation &&
    currentJob.state === 'complete'
  )
}

export function isDestructiveContentAction(
  action: DiscussionContentActionRequest['action'],
): boolean {
  return action === 'hide' || action === 'delete'
}
